import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

const ContactUs = () => {
    const { theme } = useTheme();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        setSubmitted(true);
        // Handle form submission
    };

    const contactMethods = [
        {
            icon: Mail,
            title: 'Email Support',
            description: 'Get response within 24 hours',
            value: 'support@oakhr.com',
            color: 'from-blue-500 to-cyan-500'
        },
        {
            icon: Phone,
            title: 'Phone Support',
            description: 'Mon-Fri, 9AM-6PM IST',
            value: '+91 1800-123-4567',
            color: 'from-violet-500 to-purple-500'
        },
        {
            icon: MessageSquare,
            title: 'Live Chat',
            description: 'Chat with our support team',
            value: 'Available 24/7',
            color: 'from-emerald-500 to-green-500'
        },
        {
            icon: MapPin,
            title: 'Office Address',
            description: 'Visit our headquarters',
            value: 'Bangalore, India',
            color: 'from-amber-500 to-orange-500'
        }
    ];

    const faqs = [
        {
            question: 'How quickly will I receive a response?',
            answer: 'We typically respond to all inquiries within 24 business hours. For urgent issues, our live chat is available 24/7.'
        },
        {
            question: 'Do you offer phone support?',
            answer: 'Yes, phone support is available for Enterprise and Yearly plan customers. Standard plan users can access email and chat support.'
        },
        {
            question: 'Can I schedule a demo?',
            answer: 'Absolutely! Click the "Schedule Demo" button and pick a time that works for you. Our team will walk you through all features.'
        },
        {
            question: 'Where can I find product documentation?',
            answer: 'Our comprehensive help center is available at help.oakhr.com with video tutorials, guides, and FAQs.'
        }
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            {/* Header */}
            <div className="text-center mb-10">
                <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Get in Touch
                </h1>
                <p className={`text-sm md:text-base mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Have questions? We're here to help. Reach out to us through any of these channels.
                </p>
            </div>

            {/* Contact Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {contactMethods.map((method, index) => {
                    const Icon = method.icon;
                    return (
                        <div
                            key={index}
                            className={`p-5 rounded-xl ${
                                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                            }`}
                        >
                            <div className={`p-3 rounded-lg bg-gradient-to-br ${method.color} inline-flex mb-3`}>
                                <Icon className="w-5 h-5 text-white" />
                            </div>
                            <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {method.title}
                            </h3>
                            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                {method.description}
                            </p>
                            <p className={`text-sm font-medium mt-2 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                                {method.value}
                            </p>
                        </div>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Form */}
                <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                    <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Send us a Message
                    </h2>

                    {submitted ? (
                        <div className="text-center py-8">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                                <Send className="w-8 h-8 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Message Sent!
                            </h3>
                            <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                We'll get back to you within 24 hours.
                            </p>
                            <button
                                onClick={() => setSubmitted(false)}
                                className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-700"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Your Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className={`w-full px-4 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                                            : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                    placeholder="John Doe"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Email Address *
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                    className={`w-full px-4 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                                            : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                    placeholder="john@company.com"
                                />
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Subject *
                                </label>
                                <select
                                    required
                                    value={formData.subject}
                                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                    className={`w-full px-4 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                                            : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                >
                                    <option value="">Select a topic</option>
                                    <option value="billing">Billing & Payments</option>
                                    <option value="technical">Technical Support</option>
                                    <option value="feature">Feature Request</option>
                                    <option value="integration">Integration Help</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Message *
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    className={`w-full px-4 py-2 rounded-lg border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white focus:border-blue-500'
                                            : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                    placeholder="Describe your issue or question..."
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white font-medium rounded-lg flex items-center justify-center gap-2"
                            >
                                <Send className="w-4 h-4" />
                                Send Message
                            </button>
                        </form>
                    )}
                </div>

                {/* FAQs */}
                <div>
                    <div className={`p-6 rounded-xl mb-6 ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <Clock className={`w-5 h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                Support Hours
                            </h2>
                        </div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Email Support</span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>24/7</span>
                            </div>
                            <div className="flex justify-between">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Live Chat</span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>24/7</span>
                            </div>
                            <div className="flex justify-between">
                                <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Phone Support</span>
                                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Mon-Fri, 9AM-6PM IST</span>
                            </div>
                        </div>
                    </div>

                    <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                        <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <div key={index} className="border-b dark:border-gray-700 pb-3 last:border-0 last:pb-0">
                                    <h3 className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                        {faq.question}
                                    </h3>
                                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;
